import React from "react";
import "./Footer.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faTwitter,
  faInstagram,
  faLinkedinIn,
} from "@fortawesome/free-brands-svg-icons";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLocalContext } from "../../context/LocalContext";
import {
  resolveSiteEmail,
  resolveSiteName,
  resolveSitePhone,
  SITE_DOMAIN,
  SITE_LOGO_URL,
} from "../../utils/siteConfig";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const Footer = () => {
  const { webinfo = {} } = useLocalContext();
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const address = webinfo?.address || "Remote · Worldwide";
  const email = resolveSiteEmail(webinfo);
  const phoneText = resolveSitePhone(webinfo);
  const phoneCall = String(webinfo?.phonecall || webinfo?.phone || "").trim();
  const siteName = resolveSiteName(webinfo);

  return (
    <footer className="ftr" aria-label="Footer">
      <div className="ftr__wrap">
        <motion.div
          className="ftr__cta"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
        >
          <div>
            <h3 className="ftr__ctaTitle">Ready to grow your business online?</h3>
            <p className="ftr__ctaText">
              Tell us about your project — we'll respond with a custom proposal
              and timeline within 24 hours.
            </p>
          </div>
          <div className="ftr__ctaBtns">
            <button className="ftr__btn ftr__btn--primary" onClick={() => navigate("/contact")}>
              Start a Project <span aria-hidden="true">→</span>
            </button>
            <button className="ftr__btn ftr__btn--ghost" onClick={() => navigate("/services")}>
              View Services
            </button>
          </div>
        </motion.div>

        <motion.div
          className="ftr__grid"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.22 }}
        >
          <div>
            <img className="ftr__logo" src={SITE_LOGO_URL} alt={siteName} />
            <h2 className="ftr__brandName ftr__brandName--sr">{siteName}</h2>
            <p className="ftr__brandText">
              Professional website development, digital marketing, and business
              tech solutions — built to help you succeed online.
            </p>

            <div className="ftr__meta">
              <div className="ftr__metaRow">
                <i className="ftr__icon" aria-hidden="true">📍</i>
                <div>
                  <p className="ftr__metaLabel">Location</p>
                  <p className="ftr__metaValue">{address}</p>
                </div>
              </div>
              <div className="ftr__metaRow">
                <i className="ftr__icon" aria-hidden="true">✉️</i>
                <div>
                  <p className="ftr__metaLabel">Email</p>
                  <p className="ftr__metaValue"><a href={`mailto:${email}`}>{email}</a></p>
                </div>
              </div>
              {phoneText && (
                <div className="ftr__metaRow">
                  <i className="ftr__icon" aria-hidden="true">📞</i>
                  <div>
                    <p className="ftr__metaLabel">Phone</p>
                    <p className="ftr__metaValue">
                      <a href={phoneCall ? `tel:${phoneCall}` : "#"}>{phoneText}</a>
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="ftr__socials" aria-label="Social links">
              <a className="ftr__social" href="https://www.facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook">
                <FontAwesomeIcon icon={faFacebookF} />
              </a>
              <a className="ftr__social" href="https://www.twitter.com" target="_blank" rel="noreferrer" aria-label="Twitter">
                <FontAwesomeIcon icon={faTwitter} />
              </a>
              <a className="ftr__social" href="https://www.instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram">
                <FontAwesomeIcon icon={faInstagram} />
              </a>
              <a className="ftr__social" href="https://www.linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                <FontAwesomeIcon icon={faLinkedinIn} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="ftr__h4">Services</h4>
            <ul className="ftr__links">
              <li><a href="/services">Website Development</a></li>
              <li><a href="/services">UI/UX Design</a></li>
              <li><a href="/services">SEO & Content</a></li>
              <li><a href="/services">Digital Marketing</a></li>
            </ul>
          </div>

          <div>
            <h4 className="ftr__h4">Company</h4>
            <ul className="ftr__links">
              <li><a href="/about">About Us</a></li>
              <li><a href="/our-strategies">Our Strategies</a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
          </div>

          <div>
            <h4 className="ftr__h4">Legal</h4>
            <ul className="ftr__links">
              <li><a href="/Terms-and-condition">Terms</a></li>
              <li><a href="/privacy-policy">Privacy</a></li>
              <li><a href="/disclaimer">Disclaimer</a></li>
            </ul>
          </div>
        </motion.div>

        <div className="ftr__bottom">
          <p className="ftr__copy">
            © {year} {siteName}. {SITE_DOMAIN} — Built with care.
          </p>
          <div className="ftr__miniLinks">
            <a href="/privacy-policy">Privacy</a>
            <a href="/Terms-and-condition">Terms</a>
            <a href="/contact">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
